import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, Phone, Mail, Clock, MessageSquare, 
  Send, Shield, Activity, Calendar, Smile
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import config from '../../config';
import nerusLogo from '../../Assests/nerussmall.png';

const Ticket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Pending');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Actions State
  const [newComment, setNewComment] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    const fetchTicket = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${config.API_URL}/requests/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }

        if (!response.ok) {
          if (response.status === 404) throw new Error('Ticket not found');
          throw new Error('Failed to load ticket details');
        }

        const data = await response.json();
        setTicket(data);
        
        // Capitalize status from backend to match select options
        const initialStatus = data.status ? data.status.charAt(0).toUpperCase() + data.status.slice(1) : 'Pending';
        setSelectedStatus(initialStatus);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTicket();
  }, [id, navigate]);

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleStatusSave = async () => {
    setIsUpdatingStatus(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${config.API_URL}/requests/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: selectedStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');
      
      const updatedData = await response.json();
      setTicket(updatedData);
      
      const newSavedStatus = updatedData.status ? updatedData.status.charAt(0).toUpperCase() + updatedData.status.slice(1) : 'Pending';
      setSelectedStatus(newSavedStatus);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    setIsSubmittingComment(true);
    setShowEmojiPicker(false); // Close picker on submit
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${config.API_URL}/requests/${id}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ comment_text: newComment })
      });

      if (!response.ok) throw new Error('Failed to add comment');
      
      const addedComment = await response.json();
      
      // Update local state directly instead of re-fetching to feel instantaneous
      setTicket(prev => ({
        ...prev,
        comments: [...prev.comments, addedComment]
      }));
      setNewComment('');
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center space-y-6">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white/5 p-3 shadow-[0_0_40px_rgba(34,211,238,0.1)] relative">
          <div className="absolute inset-0 border-t-2 border-cyan-400 rounded-2xl animate-spin"></div>
          <img src={nerusLogo} alt="Loading..." className="w-full h-full object-contain animate-pulse" />
        </div>
        <div className="text-cyan-400/60 font-bold tracking-widest text-sm uppercase animate-pulse">Loading Ticket #{id}</div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4">
        <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-2xl max-w-md w-full text-center">
          <p className="text-red-400 font-bold mb-4">{error || 'Ticket not found'}</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors font-medium text-sm"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isClosed = (ticket.status || '').toLowerCase() === 'closed';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans">
      {/* Top Navbar */}
      <nav className="border-b border-white/10 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 -ml-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <span className="text-lg font-black text-white">Ticket</span>
                <span className="text-lg font-mono text-cyan-400 font-bold bg-cyan-500/10 px-2 py-0.5 rounded">
                  #{ticket.id}
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column: Ticket Info & Actions */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-6">Client Details</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400 mt-1">
                    <User className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">Full Name</p>
                    <p className="text-base font-bold text-white">{ticket.name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-400 mt-1">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">Email Address</p>
                    <a href={`mailto:${ticket.email}`} className="text-base font-medium text-cyan-400 hover:underline">{ticket.email}</a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg text-purple-400 mt-1">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">Phone Number</p>
                    <a href={`tel:${ticket.mobile}`} className="text-base font-medium text-white">{ticket.mobile}</a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-500/10 rounded-lg text-orange-400 mt-1">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase mb-1">Submitted On</p>
                    <p className="text-sm font-medium text-white">
                      {new Date(ticket.timestamp).toLocaleString(undefined, { 
                        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-6">Ticket Status</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isClosed ? 'bg-emerald-500/10 text-emerald-400' : 'bg-cyan-500/10 text-cyan-400'}`}>
                    {isClosed ? <Shield className="w-5 h-5" /> : <Activity className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <select 
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      disabled={isUpdatingStatus}
                      className={`w-full bg-slate-900 border ${isClosed ? 'border-emerald-500/30 focus:border-emerald-500' : 'border-cyan-500/30 focus:border-cyan-500'} rounded-lg px-3 py-2 text-sm font-bold text-white focus:outline-none appearance-none cursor-pointer`}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Active">Active</option>
                      <option value="Closed">Closed</option>
                      <option value="Blocked">Blocked</option>
                    </select>
                  </div>
                </div>
                
                {selectedStatus !== (ticket.status ? ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1) : 'Pending') && (
                  <button 
                    onClick={handleStatusSave}
                    disabled={isUpdatingStatus}
                    className="w-full mt-3 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold py-2 rounded-lg transition-colors text-sm"
                  >
                    Save Status
                  </button>
                )}
                {isUpdatingStatus && <p className="text-xs text-slate-400 animate-pulse text-center">Updating status...</p>}
              </div>
            </div>
          </div>

          {/* Right Column: Comments Thread */}
          <div className="lg:col-span-2 flex flex-col h-[calc(100vh-8rem)]">
            <div className="bg-white/5 border border-white/10 rounded-t-2xl p-6 flex items-center gap-3 border-b border-b-white/5">
              <MessageSquare className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-bold text-white">Activity & Comments</h2>
            </div>

            {/* Comments Area (Scrollable) */}
            <div className="flex-1 bg-slate-900/50 border-x border-white/10 p-6 overflow-y-auto space-y-6">
              {(!ticket.comments || ticket.comments.length === 0) ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-500 space-y-4">
                  <MessageSquare className="w-12 h-12 opacity-20" />
                  <p>No comments on this ticket yet.</p>
                </div>
              ) : (
                ticket.comments.map((comment) => (
                  <div key={comment.id} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-white">
                        {comment.agent_email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl rounded-tl-none p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-bold text-cyan-400">{comment.agent_email}</span>
                        <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(comment.timestamp).toLocaleString(undefined, { 
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                        {comment.comment_text}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Comment Input Box */}
            <div className="bg-white/5 border border-white/10 rounded-b-2xl p-4 relative">
              {showEmojiPicker && (
                <div className="absolute bottom-[calc(100%+10px)] right-4 z-50 shadow-2xl rounded-lg overflow-hidden border border-white/10">
                  <EmojiPicker 
                    onEmojiClick={(emojiObject) => setNewComment(prev => prev + emojiObject.emoji)}
                    theme="dark"
                    searchDisabled={false}
                    skinTonesDisabled={true}
                    width={320}
                    height={400}
                  />
                </div>
              )}
              <form onSubmit={handleCommentSubmit} className="relative">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Type an internal note or update..."
                  className="w-full bg-slate-900 border border-white/10 rounded-xl pl-4 pr-24 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 resize-none"
                  rows="3"
                  disabled={isSubmittingComment}
                />
                <button 
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="absolute bottom-3 right-14 p-2 text-slate-400 hover:text-cyan-400 rounded-lg transition-colors"
                >
                  <Smile className="w-5 h-5" />
                </button>
                <button 
                  type="submit"
                  disabled={!newComment.trim() || isSubmittingComment}
                  className="absolute bottom-3 right-3 p-2 bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-700 text-slate-950 disabled:text-slate-500 rounded-lg transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Ticket;
